import { computed } from "vue";
import { storeToRefs } from "pinia";

import { useApplicationStore } from "@/stores/Application";
import { useArticlesStore } from "@/stores/Articles";
import { usePermissionsStore } from "@/stores/Permissions";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useWebinarsStore } from "@/stores/Webinars";

import { useContentReadState, type UnreadContentEntry } from "@/composables/content-read-state";
import { useMenuPermissions } from "@/composables/menu-permissions";
import { useNotificationAcknowledgement } from "@/composables/notification-acknowledgement";

import { articleKey, tradeAlertKey, webinarKey } from "@/types/products/product-definitions";

import type { ContentfulItem } from "@tmi/types/entities";

export type UnreadNotificationItem = UnreadContentEntry & {
    contentLabel: string,
    icon: string,
};

function publishedTimestamp( content: ContentfulItem ): number {
    return new Date( content.publishedDate ).getTime();
}

export function useUnreadNotifications() {
    const { allArticles } = storeToRefs( useArticlesStore() );
    const { allTradeAlerts } = storeToRefs( useTradeAlertsStore() );
    const { allWebinars } = storeToRefs( useWebinarsStore() );
    const {
        hasArticleAccess,
        hasProductTradeAlertsAccess,
        hasWebinarAccess,
    } = storeToRefs( usePermissionsStore() );
    const {
        showArticlesOption,
        showTradeAlertsMenuOption,
        showWebinarsMenuOption,
    } = useMenuPermissions();
    const {
        markAllContentRead,
        markContentRead,
        unreadRecentContentList,
    } = useContentReadState();
    const {
        setSelectedArticle,
        setSelectedTradeAlert,
        setSelectedWebinar,
    } = useApplicationStore();
    const { acknowledge, isAcknowledged: isAcknowledgedByKey } = useNotificationAcknowledgement();

    const showNotificationsMenu = computed< boolean >( () =>
        showArticlesOption.value || showTradeAlertsMenuOption.value || showWebinarsMenuOption.value
    );

    const unreadNotifications = computed< UnreadNotificationItem[] >( () => {
        const items: UnreadNotificationItem[] = [];

        if ( hasArticleAccess.value ) {
            unreadRecentContentList( allArticles.value, articleKey ).forEach( ( content: ContentfulItem ) => {
                items.push( {
                    content,
                    contentType: articleKey,
                    contentLabel: 'Article',
                    icon: 'pi-folder',
                } );
            } );
        }

        if ( hasProductTradeAlertsAccess.value ) {
            unreadRecentContentList( allTradeAlerts.value, tradeAlertKey ).forEach( ( content: ContentfulItem ) => {
                items.push( {
                    content,
                    contentType: tradeAlertKey,
                    contentLabel: 'Trade Alert',
                    icon: 'pi-chart-line',
                } );
            } );
        }

        if ( hasWebinarAccess.value ) {
            unreadRecentContentList( allWebinars.value, webinarKey ).forEach( ( content: ContentfulItem ) => {
                items.push( {
                    content,
                    contentType: webinarKey,
                    contentLabel: 'Webinar',
                    icon: 'pi-video',
                } );
            } );
        }

        return items.sort( ( a: UnreadNotificationItem, b: UnreadNotificationItem ) =>
            publishedTimestamp( b.content ) - publishedTimestamp( a.content )
        );
    } );

    // Bell badge decrements as the user acknowledges items — either by
    // hovering a row in the dropdown (acknowledgement is persisted to
    // localStorage) or by clicking a row to open it (markContentRead
    // drops it from unreadNotifications entirely). The list itself
    // still shows every unread item so a hovered notification remains
    // clickable; only the counter shrinks.
    const totalUnreadCount = computed< number >( () =>
        unreadNotifications.value.reduce( ( count, item ) =>
            isAcknowledgedByKey( item.content, item.contentType ) ? count : count + 1,
        0 )
    );

    function markAllAsRead(): void {
        if ( hasArticleAccess.value ) {
            markAllContentRead( allArticles.value, articleKey );
        }
        if ( hasProductTradeAlertsAccess.value ) {
            markAllContentRead( allTradeAlerts.value, tradeAlertKey );
        }
        if ( hasWebinarAccess.value ) {
            markAllContentRead( allWebinars.value, webinarKey );
        }
    }

    function isAcknowledged( item: UnreadNotificationItem ): boolean {
        return isAcknowledgedByKey( item.content, item.contentType );
    }

    function acknowledgeItem( item: UnreadNotificationItem ): void {
        acknowledge( item.content, item.contentType );
    }

    function openNotification( item: UnreadNotificationItem ): void {
        const { content, contentType } = item;
        if ( !content.contentfulId ) {
            return;
        }

        markContentRead( content, contentType );

        switch ( contentType ) {
            case articleKey:
                setSelectedArticle( content.contentfulId );
                break;
            case tradeAlertKey:
                setSelectedTradeAlert( content.contentfulId );
                break;
            case webinarKey:
                setSelectedWebinar( content.contentfulId );
                break;
            default:
                break;
        }
    }

    return {
        acknowledgeItem,
        isAcknowledged,
        markAllAsRead,
        openNotification,
        showNotificationsMenu,
        totalUnreadCount,
        unreadNotifications,
    };
}
